import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { toast } from '@/hooks/use-toast';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Timeout (ms) for the token-refresh request in the 401 interceptor.
 *
 * CRÍTICO: o refresh usa o `axios` bruto (não a instância `api`, que já tem
 * `timeout: 30000`). Sem um timeout explícito, um `/auth/refresh` pendurado
 * (rede/proxy sem resposta) deixaria `isRefreshing` preso em `true` para
 * sempre — o `finally` que o reseta nunca roda — orfanando toda a `failedQueue`
 * e travando o `checkAuth` → `ProtectedRoute` fica eternamente em "Carregando...".
 * Com o limite, a Promise SEMPRE liquida: em timeout, cai no catch, drena a fila
 * e redireciona para `/login` em vez de pendurar.
 */
const AUTH_REFRESH_TIMEOUT_MS = 15000;

/**
 * Public API base URL — import this instead of duplicating the VITE_API_URL fallback.
 */
export const API_BASE_URL = API_URL;

/**
 * Derive WebSocket base URL from the API configuration.
 * Handles both absolute URLs (production) and relative paths (Vite proxy dev).
 */
export function getWebSocketBaseUrl(): string {
  if (API_URL.startsWith('http://') || API_URL.startsWith('https://')) {
    return new URL(API_URL).origin;
  }
  // Relative URL: use current window location (works with Vite proxy)
  return window.location.origin;
}

/**
 * Token getter functions — set by auth store to avoid circular dependency
 */
let getAuthToken: (() => string | null) | null = null;
let getRefreshTokenFn: (() => string | null) | null = null;
let setTokensFn: ((accessToken: string, refreshToken?: string) => void) | null = null;
let getTenantIdFn: (() => string | null) | null = null;
let getSelectedOrgIdFn: (() => string | null) | null = null;

export const setGetAuthToken = (fn: () => string | null) => {
  getAuthToken = fn;
};

export const setGetRefreshToken = (fn: () => string | null) => {
  getRefreshTokenFn = fn;
};

export const setSetTokens = (fn: (accessToken: string, refreshToken?: string) => void) => {
  setTokensFn = fn;
};

export const setGetTenantId = (fn: () => string | null) => {
  getTenantIdFn = fn;
};

/**
 * MASTER org selector bridge — set by the auth store to read the currently
 * selected organization id from {@link useMasterSystemStore} without importing
 * the store here (avoids a store↔api circular import, mirroring `setGetTenantId`).
 */
export const setGetSelectedOrgId = (fn: () => string | null) => {
  getSelectedOrgIdFn = fn;
};

/**
 * Billing-block bridge (Epic #884 §3.1) — set by the billing-block store.
 *
 * The interceptor calls this on 402 (`BILLING_*` paywall) or 403 `ORG_SUSPENDED`
 * with the event, or with `null` to clear the block on any successful response.
 * Kept as a callback to avoid a circular import (store → api → store).
 */
export interface BillingBlockEvent {
  kind: 'paywall' | 'org_suspended';
  code?: string | null;
  message?: string | null;
}
let billingBlockHandler: ((event: BillingBlockEvent | null) => void) | null =
  null;

export const setBillingBlockHandler = (
  fn: (event: BillingBlockEvent | null) => void,
) => {
  billingBlockHandler = fn;
};

/**
 * Module-level mirror of the billing-block store (Epic #884 §3.1).
 *
 * Kept in sync by the store via {@link setBillingBlockState} (store → api
 * direction, mirroring the `setBillingBlockHandler` bridge in the api → store
 * direction). We intentionally do NOT import the store here to avoid the
 * circular dependency (store imports api for the handler bridge).
 *
 * The REQUEST interceptor reads this to short-circuit every non-allowlisted
 * request while blocked, so NO poller / background query reaches the backend
 * during the paywall (kills `/status` + counters + the 402/2xx toggle that
 * caused the PaywallScreen flicker).
 */
let activeBillingBlock: BillingBlockEvent | null = null;

export const setBillingBlockState = (state: BillingBlockEvent | null) => {
  activeBillingBlock = state;
};

/**
 * Marker carried by the synthetic error the request interceptor throws when it
 * blocks a non-allowlisted request during the paywall. Lets the Sentry capture
 * + query error toast paths recognize and drop it (it is NOT a real failure —
 * the request never left the browser). Also matched by axios `isCancel`.
 */
export const BILLING_BLOCKED_ERROR_CODE = 'ERR_BILLING_BLOCKED';

interface BillingBlockedMarker {
  __billingBlocked: true;
  code: typeof BILLING_BLOCKED_ERROR_CODE;
}

/**
 * True for the synthetic "request blocked by paywall" error produced below.
 * Recognizes both the explicit `__billingBlocked` marker and an axios Cancel
 * (we throw an `axios.Cancel` so the request is treated as cancelled, not a
 * network failure).
 */
export function isBillingBlockedError(error: unknown): boolean {
  if (axios.isCancel(error)) return true;
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as Partial<BillingBlockedMarker>).__billingBlocked === true
  );
}

/** Build the synthetic, benign error used to short-circuit blocked requests. */
function createBillingBlockedError(): BillingBlockedMarker {
  return Object.assign(new axios.Cancel('billing-blocked'), {
    __billingBlocked: true as const,
    code: BILLING_BLOCKED_ERROR_CODE as typeof BILLING_BLOCKED_ERROR_CODE,
  });
}

/**
 * Path prefixes the backend `BillingStatusGuard` NEVER gates (mirror of
 * `ALLOWLISTED_PATH_PREFIXES`). Kept WITHOUT the `/api` global prefix because
 * frontend callers pass URLs relative to `baseURL` (`/api`), e.g. `/billing/...`.
 *
 * A successful response on one of these endpoints does NOT mean the paywall is
 * lifted (the backend would let them through even while blocked), so we must
 * NOT clear an active billing block on them — otherwise the paywall flickers
 * or gets stale-unblocked while polling `/billing/*` or refreshing auth.
 */
const BILLING_ALLOWLISTED_PATH_PREFIXES = [
  '/billing',
  '/auth',
  '/webhooks',
  '/health',
] as const;

/**
 * True when the request URL targets an endpoint the backend never gates — in
 * which case a 2xx must NOT clear the billing block. Tolerates an optional
 * leading `/api` prefix and a query string.
 */
export function isBillingAllowlistedUrl(url?: string): boolean {
  if (!url) return false;
  // Drop origin (absolute URLs), query string and hash; keep the pathname.
  let path = url;
  try {
    if (/^https?:\/\//i.test(path)) {
      path = new URL(path).pathname;
    }
  } catch {
    // not a parseable absolute URL — fall through with the raw value
  }
  path = path.split('?')[0].split('#')[0];
  // Normalize an optional `/api` global prefix (defensive — callers usually omit it).
  if (path === '/api') path = '/';
  else if (path.startsWith('/api/')) path = path.slice('/api'.length);
  if (!path.startsWith('/')) path = `/${path}`;
  return BILLING_ALLOWLISTED_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

/**
 * Axios instance configured for the Atriva API
 */
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

/**
 * Request interceptor — add auth token and tenant-id
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Billing block (Epic #884 §3.1): while access is blocked, reject every
    // non-allowlisted request BEFORE it is sent. This stops all pollers
    // (`/status`, counters, badges) from hitting the backend during the paywall
    // — killing the log/Sentry noise AND the 402/2xx toggle that flickered the
    // PaywallScreen. Allowlisted paths (`/billing`, `/auth`, `/webhooks`,
    // `/health`) ALWAYS pass so the customer can see billing, pay and refresh
    // tokens — without them the user would be permanently locked out (the
    // unblock is driven by `/billing/access`, which is allowlisted).
    if (activeBillingBlock && !isBillingAllowlistedUrl(config.url)) {
      return Promise.reject(createBillingBlockedError());
    }

    const token = getAuthToken ? getAuthToken() : null;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add tenant-id header from authenticated user context
    // Skip for MASTER users (they don't belong to a specific tenant)
    const tenantId = getTenantIdFn ? getTenantIdFn() : null;
    if (config.headers && tenantId) {
      config.headers['x-tenant-id'] = tenantId;
    }

    // MASTER org selector: scope /system/* requests to the selected org's DB.
    // Gated to /system routes so the org-list fetch (/organizations) stays
    // unscoped — the dropdown must always list every org.
    const selectedOrgId = getSelectedOrgIdFn ? getSelectedOrgIdFn() : null;
    if (config.headers && selectedOrgId && config.url?.startsWith('/system')) {
      config.headers['x-selected-organization-id'] = selectedOrgId;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

/**
 * Response interceptor — auto-refresh token on 401
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    }
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => {
    // A successful API call on a GATED endpoint means access is allowed again —
    // clear any paywall / org-suspended block (e.g. after the tenant pays or the
    // org reactivates). Allowlisted endpoints (`/billing/*`, `/auth/*`,
    // `/webhooks/*`, `/health/*`) are NEVER gated by the backend, so a 2xx there
    // says nothing about billing — clearing on them would cause paywall flicker
    // / stale unblock while the user polls billing or refreshes auth.
    if (billingBlockHandler && !isBillingAllowlistedUrl(response.config?.url)) {
      billingBlockHandler(null);
    }
    return response;
  },
  async (error: AxiosError<{ success?: boolean; error?: { code?: string; message?: string }; message?: string; code?: string }>) => {
    // Synthetic "request blocked by paywall" error from the request interceptor:
    // it never hit the backend, so skip all response-error handling (no
    // refresh/redirect, no toast, no billing-handler mutation) and reject as-is.
    // The query error handler + Sentry capture also recognize it and drop it.
    if (isBillingBlockedError(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Auto-refresh token on 401 (skip for refresh/login endpoints)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      const refreshToken = getRefreshTokenFn ? getRefreshTokenFn() : null;

      if (refreshToken) {
        if (isRefreshing) {
          // Queue this request until refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                resolve(api(originalRequest));
              },
              reject,
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const { data } = await axios.post(
            `${API_URL}/auth/refresh`,
            { refreshToken },
            { timeout: AUTH_REFRESH_TIMEOUT_MS },
          );
          const newToken = data.accessToken as string;

          if (setTokensFn) {
            setTokensFn(newToken, data.refreshToken as string | undefined);
          }

          processQueue(null, newToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Refresh failed — clear auth and redirect
          localStorage.removeItem('auth-storage');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // No refresh token — redirect to login
      localStorage.removeItem('auth-storage');
      window.location.href = '/login';
    }

    // Handle business logic errors (limits exceeded)
    // Backend response format: { success: false, error: { code, message, details }, timestamp }
    const errorData = error.response?.data;
    const errorCode = errorData?.error?.code || errorData?.code;
    const errorMessage = errorData?.error?.message || errorData?.message;

    // Enrich error.message with backend's descriptive message so React Query
    // onError handlers display it correctly instead of generic "Request failed..."
    if (errorMessage) {
      error.message = errorMessage;
    }

    // Billing access gate (Epic #884 §3.1):
    //  - 402 BILLING_* → paywall (tenant sem pagamento) — CTA de cartão.
    //  - 403 ORG_SUSPENDED → white-label indisponível — tela neutra, sem CTA.
    const status = error.response?.status;
    if (billingBlockHandler) {
      if (status === 402) {
        billingBlockHandler({
          kind: 'paywall',
          code: errorCode ?? 'BILLING_PAYMENT_REQUIRED',
          message: errorMessage ?? null,
        });
      } else if (status === 403 && errorCode === 'ORG_SUSPENDED') {
        billingBlockHandler({
          kind: 'org_suspended',
          code: errorCode,
          message: errorMessage ?? null,
        });
      }
    }

    if (errorCode && errorCode.startsWith('BUSINESS_')) {
      toast({
        variant: 'destructive',
        title: 'Limite atingido',
        description: errorMessage || 'Operação não permitida devido a limite do plano.',
      });
    }

    return Promise.reject(error);
  },
);